import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import { useCreateMutation, useDeleteMutation, useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

interface TenantRequest {
  id: string;
  businessName: string;
  contactEmail: string;
  contactPhone: string;
  requestedSubDomain: string;
  status: string;
  createdAt: string;
}

const TenantRequestList: React.FC = () => {
  const { data, isLoading, isError } = useListQuery<TenantRequest>(
    `${API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE}`,
  );

  const tenantRequests: TenantRequest[] = useMemo(() => {
    if (!data) return [];
    return data.map((item: any) => ({
      id: item.id,
      businessName: item.businessName || '-',
      contactEmail: item.contactEmail || '-',
      contactPhone: item.contactPhone || '-',
      status: item.status || '-',
      requestedSubDomain: item.requestedSubDomain || '-',
      createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-',
    }));
  }, [data]);

  const columns: ColumnDef<TenantRequest>[] = useMemo(
    () => [
      {
        header: 'Business Name',
        accessorKey: 'businessName',
        cell: ({ row }) => (
          <Link to={`/tenant-requests/${row.original.id}`} className="text-dark fw-bold">
            {row.original.businessName}
          </Link>
        ),
      },
      {
        header: 'Email',
        accessorKey: 'contactEmail',
      },
      {
        header: 'Phone Number',
        accessorKey: 'contactPhone',
      },
      {
        header: 'Requested Subdomain',
        accessorKey: 'requestedSubDomain',
      },
      {
        header: 'Status',
        accessorKey: 'status',
      },
      {
        header: 'Requested On',
        accessorKey: 'createdAt',
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const { id, status } = row.original;
          const { mutate: approveTenant } = useCreateMutation(
            API_ENDPOINTS.SAAS.TENANT_REQUEST.APPROVE(id),
            API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE,
          );

          const { mutate: rejectTenant } = useCreateMutation(
            API_ENDPOINTS.SAAS.TENANT_REQUEST.REJECT(id),
            API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE,
          );

          const { mutate: deleteTenant } = useDeleteMutation(
            API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE,
            API_ENDPOINTS.SAAS.TENANT_REQUEST.BASE,
          );

          const handleApprove = () => {
            approveTenant(id);
          };

          const handleReject = () => {
            const rejectionReason = prompt('Please enter a reason for rejection');
            if (rejectionReason) {
              rejectTenant({ id, rejectionReason });
            }
          };

          const handleDelete = () => {
            if (window.confirm('Are you sure you want to delete this request?')) {
              deleteTenant(id);
            }
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              {status === 'Pending' ? (
                <>
                  <i
                    className="bx bx-check text-success cursor-pointer"
                    title="Approve"
                    style={{ fontSize: '1.2rem' }}
                    onClick={handleApprove}
                  ></i>
                  <i
                    className="bx bx-x text-danger cursor-pointer"
                    title="Reject"
                    style={{ fontSize: '1.2rem' }}
                    onClick={handleReject}
                  ></i>
                </>
              ) : (
                <i
                  className="bx bx-trash text-muted cursor-pointer"
                  title="Delete"
                  style={{ fontSize: '1.2rem' }}
                  onClick={handleDelete}
                ></i>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Tenants" breadcrumbItem="Requests" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    Failed to load tenant requests. Please try again later.
                  </Alert>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={tenantRequests}
                    isGlobalFilter={true}
                    isAddButton={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    buttonClass="btn btn-success btn-rounded"
                    buttonName="Add Tenant Request"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    paginationClass="pagination"
                    SearchPlaceholder="Search tenant requests..."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TenantRequestList;
